import { createRef, Fragment, h } from "preact";
import { useEffect, useState } from "preact/hooks";
import { classNames, displayMoney, renderHtml } from "../../helpers.js";
import Button from "../Button.jsx";

import CircularButton from "../CircularButton.jsx";
import HelpIcon from "../HelpIcon.jsx";
import Icon from "../Icon.jsx";
import Stars from "../Stars.jsx";

const COMPONENTS = {
  Stars,
};

function renderData(products, key, fieldClass) {
  return (
    <Fragment>
      {products.map((product) => {
        let field = product.data[key];
        switch (typeof product.data[key]) {
          case "boolean":
            field = <Icon children={product.data[key] ? "check" : "clear"} />;
            break;
          case "object":
            if (Array.isArray(product.data[key])) {
              field = (
                <Fragment>
                  {product.data[key][0]}
                  {product.data[key][1] && (
                    <HelpIcon>{renderHtml(product.data[key][1])}</HelpIcon>
                  )}
                </Fragment>
              );
            } else {
              const Component = COMPONENTS[product.data[key].type];
              field = <Component {...product.data[key]} />;
            }
            break;
          default:
        }
        return <div class={fieldClass}>{field}</div>;
      })}
    </Fragment>
  );
}

const BREAKPOINT = 768;
const NAVI_THRESHOLD = 0.33; // in percent

export default function HealthInsuranceChooser({ products }) {
  const [offset, setOffset] = useState(0);
  const [sliceWidth, setSliceWidth] = useState(
    window.innerWidth < BREAKPOINT ? 1 : 3
  );
  const [leftHovered, setLeftHovered] = useState(false);
  const [rightHovered, setRightHovered] = useState(false);
  const productSlice = products.slice(offset, sliceWidth + offset);
  let resizeTimeout = null;

  function handleSliceChange(increment) {
    setOffset(offset + increment);
  }

  function onMouseMove(event) {
    setLeftHovered(event.clientX < window.innerWidth * NAVI_THRESHOLD);
    setRightHovered(event.clientX > window.innerWidth * (1 - NAVI_THRESHOLD));
  }

  function onResize() {
    if (resizeTimeout) window.clearTimeout(resizeTimeout);
    resizeTimeout = window.setTimeout(() => {
      if (window.innerWidth < BREAKPOINT) {
        setSliceWidth(1);
      } else {
        setSliceWidth(3);
        setOffset(Math.min(offset, products.length - 3));
      }
    }, 150);
  }

  useEffect(() => {
    window.document.addEventListener("mousemove", onMouseMove);
    window.addEventListener("resize", onResize);

    return () => {
      window.document.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("resize", onResize);
    };
  });

  const hasMoreAfter = offset + sliceWidth < products.length;
  const hasMoreBefore = offset > 0;

  const labelClass = classNames(
    "product-chooser__label col s6",
    sliceWidth > 1 && "m3"
  );
  const fieldClass = classNames(
    "col s6 product-chooser__field",
    sliceWidth > 1 && "m3"
  );

  return (
    <Fragment>
      {hasMoreBefore && (
        <CircularButton
          circular
          trackingName="chooserGoLeft"
          class={classNames(
            "product-chooser__backward",
            leftHovered && "product-chooser__backward--visible"
          )}
          onClick={() => handleSliceChange(-1)}
        >
          arrow_back
        </CircularButton>
      )}
      {hasMoreAfter && (
        <CircularButton
          circular
          trackingName="chooserGoLeft"
          class={classNames(
            "product-chooser__forward",
            rightHovered && "product-chooser__forward--visible"
          )}
          onClick={() => handleSliceChange(1)}
        >
          arrow_forward
        </CircularButton>
      )}

      <div class="row product-chooser__row">
        <div class={classNames("col s6", sliceWidth > 1 && "m3")}></div>
        {productSlice.map((product) => (
          <div class={classNames("col s6", sliceWidth > 1 && "m3")}>
            <h6 class="t-h6 product-chooser__header">
              <img
                src={`/images/health_insurance_logos/${product.id}.png`}
                width="24"
                height="24"
              />
              {product.name}
            </h6>
          </div>
        ))}
      </div>
      <div class="row product-chooser__row">
        <div class={labelClass}>Your contribution</div>
        {productSlice.map((product) => (
          <div
            class={classNames(
              "product-chooser__price col s6",
              sliceWidth > 1 && "m3"
            )}
          >
            <span>
              {product.data.your_contribution
                ? displayMoney(product.data.your_contribution / 12, "EUR")
                : "?"}
            </span>{" "}
            per month
          </div>
        ))}
      </div>
      <div class="row product-chooser__row">
        <div class={labelClass}>English support</div>
        {renderData(productSlice, "english_support", fieldClass)}
      </div>
      <div class="row product-chooser__row">
        <div class={labelClass}>Processing speed</div>
        {renderData(productSlice, "processing_speed", fieldClass)}
      </div>
      <div class="row product-chooser__row">
        <div class={labelClass}>
          Social security number delivery
          <HelpIcon>
            <h4 class="t-h6">Social security number delivery</h4>

            <p class="t-body-3">
              Your SVNR or Sozialversicherungsnummer is your social security
              number, which is required by employers to be able to pay their
              employees. If you don’t already have an SVNR, yours will be
              generated shortly after your health insurance coverage is
              confirmed. It will be sent to your address via post within a
              couple of weeks.
            </p>
          </HelpIcon>
        </div>
        {renderData(productSlice, "ssn_deliver", fieldClass)}
      </div>
      <div class="row product-chooser__row">
        <div class={labelClass}></div>
        {productSlice.map((product) => (
          <div class={fieldClass}>
            <Button secondary href={product.data.signup_link}>
              Sign up
            </Button>
          </div>
        ))}
      </div>

      <div class="product-chooser__section-header">General</div>

      <div class="row product-chooser__row">
        <div class={labelClass}>
          General doctor visits
          <HelpIcon>
            <p class="t-body-3">
              For example: general practitioner, eye doctor, gynecologist,
              pediatrician, emergency doctor
            </p>
          </HelpIcon>
        </div>
        {renderData(productSlice, "general_doctor_visits", fieldClass)}
      </div>
      <div class="row product-chooser__row">
        <div class={labelClass}>
          Specialist doctor visits
          <HelpIcon>
            <p class="t-body-3">
              For example: dermatologist, orthopedic, Ear-Nose-Throat doctor
            </p>
          </HelpIcon>
        </div>
        {renderData(productSlice, "special_doctir_visits", fieldClass)}
      </div>
      <div class="row product-chooser__row">
        <div class={labelClass}>Medication</div>
        {renderData(productSlice, "medication", fieldClass)}
      </div>
      <div class="row product-chooser__row">
        <div class={labelClass}>
          General vaccinations
          <HelpIcon>
            <p class="t-body-3">
              The following vaccinations are considered general vaccinations in
              Germany:
            </p>
            <ul>
              <li>Chicken pox</li>
              <li>Diphtheria</li>
              <li>Hib (Haemophilus influenzae type B)</li>
              <li>HPV</li>
              <li>
                Measles - Since 2020 it is mandatory by law to get this
                vaccination
              </li>
              <li>Mumps</li>
              <li>Pneumococcus</li>
              <li>Polio</li>
              <li>Rotavirus</li>
              <li>Rubella</li>
              <li>Shingles (zoster)</li>
              <li>Tetanus</li>
              <li>Whooping cough (pertussis)</li>
            </ul>
          </HelpIcon>
        </div>
        {renderData(productSlice, "general_vaccinations", fieldClass)}
      </div>
      <div class="row product-chooser__row">
        <div class={labelClass}>
          Travel vaccinations
          <HelpIcon>
            <p class="t-body-3">
              The following vaccinations are considered travel vaccinations in
              Germany:
            </p>
            <ul>
              <li>Cholera</li>
              <li>TBE (Tick-borne encephalitis)</li>
              <li>Yellow fever</li>
              <li>Hepatitis A</li>
              <li>Hepatitis B</li>
              <li>Japanese encephalitis</li>
              <li>Malaria prophylaxis</li>
              <li>Meningococcus type ACWY</li>
              <li>Meningococcus type C</li>
              <li>Rabies</li>
              <li>Typhus</li>
            </ul>
          </HelpIcon>
        </div>
        {renderData(productSlice, "travel_vaccinations", fieldClass)}
      </div>
      <div class="row product-chooser__row">
        <div class={labelClass}>
          Transports
          <HelpIcon>
            <p class="t-body-3">
              In the event of an emergency, ambulance, taxi or Uber to and from
              the closest suitable hospital or clinic.
            </p>
            <p class="t-body-3">
              Non hospital transports only for dialysis or chemotherapy in the
              event of an emergency
            </p>
          </HelpIcon>
        </div>
        {renderData(productSlice, "transports", fieldClass)}
      </div>
      <div class="product-chooser__section-header">Dental</div>
      <div class="row product-chooser__row">
        <div class={labelClass}>Dental checkups</div>
        {renderData(productSlice, "dental_checkups", fieldClass)}
      </div>
      <div class="row product-chooser__row">
        <div class={labelClass}>
          Dental cleanings{" "}
          <HelpIcon>
            <p class="t-body-3">Prophylaxis not covered on Public</p>
          </HelpIcon>
        </div>
        {renderData(productSlice, "dental_cleanings", fieldClass)}
      </div>
      <div class="row product-chooser__row">
        <div class={labelClass}>
          Dentist treatment{" "}
          <HelpIcon>
            <p class="t-body-3">E.g. fillings</p>
          </HelpIcon>
        </div>
        {renderData(productSlice, "dentist_treatments", fieldClass)}
      </div>
      <div class="row product-chooser__row">
        <div class={labelClass}>
          Tooth replacement{" "}
          <HelpIcon>
            <p class="t-body-3">False teeth e.g. crowns, implants</p>
          </HelpIcon>
        </div>
        {renderData(productSlice, "tooth_replacement", fieldClass)}
      </div>

      <div class="product-chooser__section-header">Treatment and therapies</div>

      <div class="row product-chooser__row">
        <div class={labelClass}>
          Preventive care treatments
          <HelpIcon>
            <p class="t-body-3">
              Most preventive care treatments are only available after the age
              of 35 (full body checkup, skin cancer screening). There are no age
              restrictions for annual gynaecological check-ups. Here is{" "}
              <a href="https://www-krankenkassen-de.translate.goog/gesetzliche-krankenkassen/leistungen-gesetzliche-krankenkassen/gesetzlich-vorgeschriebene-leistungen/gesetzliche-krankenkassen-Vorsorgeuntersuchungen/?_x_tr_sl=auto&_x_tr_tl=en&_x_tr_hl=de">
                a complete list
              </a>
              .
            </p>
          </HelpIcon>
        </div>
        {renderData(productSlice, "preventive_care_treatments", fieldClass)}
      </div>
      <div class="row product-chooser__row">
        <div class={labelClass}>
          Breast cancer screening
          <HelpIcon>
            <p class="t-body-3">
              30 years and older can receive a yearly breast cancer screening
            </p>
            <p class="t-body-3">
              50 - 69 years can also receive a yearly mammography
            </p>
          </HelpIcon>
        </div>
        {renderData(productSlice, "breast_cancer_screening", fieldClass)}
      </div>
      <div class="row product-chooser__row">
        <div class={labelClass}>
          Skin cancer screening
          <HelpIcon>
            <p class="t-body-3">
              From age 50 -yearly immunological stool screening
            </p>
            <p class="t-body-3">
              Men from age 50 and women from age 55 can have a preventive
              colonoscopy once every 10 years{" "}
            </p>
          </HelpIcon>
        </div>
        {renderData(productSlice, "bowel_cancel_screening", fieldClass)}
      </div>
      <div class="row product-chooser__row">
        <div class={labelClass}>
          Skin cancer screening
          <HelpIcon>
            <p class="t-body-3">Free screening every 2 years from age 35 up</p>
          </HelpIcon>
        </div>
        {renderData(productSlice, "skin_cancel_screening", fieldClass)}
      </div>
      <div class="row product-chooser__row">
        <div class={labelClass}>
          Therapeutic measures
          <HelpIcon>
            <p class="t-body-3">E.g. physio therapy or speech therapy</p>
            <p class="t-body-3">
              The amount of prescribed sessions for a condition (e.g. physio
              therapy) is usually limited to a handful of sessions for most
              conditions.
            </p>
          </HelpIcon>
        </div>
        {renderData(productSlice, "therapeutic_measures", fieldClass)}
      </div>
      <div class="row product-chooser__row">
        <div class={labelClass}>
          Mental health therapy
          <HelpIcon>
            <p class="t-body-3">
              Pre-approval required. Finding a therapist may be difficult.
            </p>
          </HelpIcon>
        </div>
        {renderData(productSlice, "mental_health_therapy", fieldClass)}
      </div>
      <div class="row product-chooser__row">
        <div class={labelClass}>Natural health treatments</div>
        {renderData(productSlice, "natural_health_treatments", fieldClass)}
      </div>
      <div class="row product-chooser__row">
        <div class={labelClass}>
          Medical aids
          <HelpIcon>
            <p class="t-body-3">Medical aids: 90%</p>
            <p class="t-body-3">Hearing aids: 75%, up to €780</p>
          </HelpIcon>
        </div>
        {renderData(productSlice, "medical_aids", fieldClass)}
      </div>

      <div class="product-chooser__section-header">Vision</div>

      <div class="row product-chooser__row">
        <div class={labelClass}>
          Vision aids
          <HelpIcon>
            <p class="t-body-3">E.g. glasses or contact lenses</p>
            <p class="t-body-3">
              You can claim every two years or if your eyesight worsens by a
              power of 0.5 within a year
            </p>
          </HelpIcon>
        </div>
        {renderData(productSlice, "vision_aids", fieldClass)}
      </div>
      <div class="row product-chooser__row">
        <div class={labelClass}>
          Refractive eye surgery
          <HelpIcon>
            <p class="t-body-3">E.g. LASIK</p>
            <p class="t-body-3">Per eye every 5 years</p>
          </HelpIcon>
        </div>
        {renderData(productSlice, "eye_surgery", fieldClass)}
      </div>

      <div class="product-chooser__section-header">
        Pregnancy and childbirth
      </div>
      <div class="row product-chooser__row">
        <div class={labelClass}>General pregnancy cover</div>
        {renderData(productSlice, "general_pregnancy_cover", fieldClass)}
      </div>
      <div class="row product-chooser__row">
        <div class={labelClass}>
          Extra screenings
          <HelpIcon>
            <p class="t-body-3">
              Pregnancy care on public health insurance covers a wide variety of
              medically necessary screenings set by the German government. These
              are the same regardless of the provider you choose.
            </p>
            <p class="t-body-3">
              There are however supplemental tests that the different providers
              may offer.
            </p>
            <ul>
              <li>These tests are:</li>
              <li>Chickenpox antibody test</li>
              <li>Fifth disease antibody test</li>
              <li>First trimester screening</li>
              <li>Neck fold measurement</li>
              <li>Strep antibody test</li>
              <li>Toxoplasmosis test</li>
              <li>Triple-Test</li>
              <li>Extra ultrasound screening</li>
            </ul>
          </HelpIcon>
        </div>
        {renderData(productSlice, "extra_screening", fieldClass)}
      </div>
      <div class="row product-chooser__row">
        <div class={labelClass}>
          Fertility treatment
          <HelpIcon>
            <p class="t-body-3">
              Fertility treatments such as in-vitro fertilisation (IVF) are
              sometimes partially covered for married couples (age 25-40).
              Pre-approval is required.
            </p>
          </HelpIcon>
        </div>
        {renderData(productSlice, "fertility_treatment", fieldClass)}
      </div>
      <div class="row product-chooser__row">
        <div class={labelClass}>
          Help by a midwife
          <HelpIcon>
            <p class="t-body-3">Before and after giving birth</p>
          </HelpIcon>
        </div>
        {renderData(productSlice, "midwife", fieldClass)}
      </div>
      <div class="row product-chooser__row">
        <div class={labelClass}>Midwife on call</div>
        {renderData(productSlice, "midwife_on_call", fieldClass)}
      </div>
      <div class="row product-chooser__row">
        <div class={labelClass}>Birth preparation course for partner</div>
        {renderData(productSlice, "birth_prep_course", fieldClass)}
      </div>

      <div class="product-chooser__section-header">Hospital</div>
      <div class="row product-chooser__row">
        <div class={labelClass}>
          Board and room
          <HelpIcon>
            <p class="t-body-3">At the hospital</p>
          </HelpIcon>
        </div>
        {renderData(productSlice, "hospital", fieldClass)}
      </div>
      <div class="row product-chooser__row">
        <div class={labelClass}>
          Free choice of hospital
          <HelpIcon>
            <p class="t-body-3">All public hospitals</p>
          </HelpIcon>
        </div>
        {renderData(productSlice, "free_choice_of_hospital", fieldClass)}
      </div>
      <div class="row product-chooser__row">
        <div class={labelClass}>Private hospitals</div>
        {renderData(productSlice, "private_hospital", fieldClass)}
      </div>

      <div class="product-chooser__section-header">Outside of Germany</div>
      <div class="row product-chooser__row">
        <div class={labelClass}>
          EU-wide cover
          <HelpIcon>
            <p class="t-body-3">
              For stays up to 6 months in European countries including the EEA
              (Liechtenstein, Norway and Switzerland) using the European Health
              Insurance Card (EHIK)
            </p>
          </HelpIcon>
        </div>
        {renderData(productSlice, "eu_cover", fieldClass)}
      </div>
      <div class="row product-chooser__row">
        <div class={labelClass}>Worldwide cover</div>
        {renderData(productSlice, "worldwide_cover", fieldClass)}
      </div>
      <div class="row product-chooser__row">
        <div class={labelClass}>Holiday travel cover</div>
        {renderData(productSlice, "holiday_travel_cover", fieldClass)}
      </div>
      <div class="row product-chooser__row">
        <div class={labelClass}>Repatriation costs</div>
        {renderData(productSlice, "repratriation_costs", fieldClass)}
      </div>
      <div class="row">
        <div class="col s12 m8">
          <p class="t-body-3 mt-48 mb-48">
            <strong>Please note:</strong> You can directly obtain your health
            insurance online. Just click on the provided link of the insurance
            you have selected and follow the online application process. After
            you have submitted your application, it usually takes 5-10 days
            until the insurance company reaches out to you. All communication
            will be in English.
            <br />
            Have you obtained health insurance? Please pass this information on
            to Nioomi.
          </p>
        </div>
      </div>
    </Fragment>
  );
}
